import { Money, type Currency } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form } from 'antd';
import type { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { AmountFields } from '../../../components/AmountFields';
import { FreeQuantityField } from './FreeQuantityField';
import { supersedeAccessorialRate, type AccessorialRateView, type AccessorialType } from '../../../rate-cards.api';

interface ChangeFormValues {
  amount: number;
  currency: Currency;
  freeQuantity?: number;
  effectiveDate: Dayjs;
}

interface ChangeAccessorialRateDrawerProps {
  rate: AccessorialRateView | null;
  type: AccessorialType | undefined;
  onChanged: () => void;
  onClose: () => void;
}

/** A new price for the same charge type, effective at once from the date given; the old rate is closed the day before and kept. */
export function ChangeAccessorialRateDrawer({ rate, type, onChanged, onClose }: ChangeAccessorialRateDrawerProps) {
  const [form] = Form.useForm<ChangeFormValues>();
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  useEffect(() => {
    if (!rate) return;
    form.setFieldsValue({
      amount: Number(Money.fromJSON(rate.amount).toDecimalString()),
      currency: rate.amount.currency,
      freeQuantity: rate.freeQuantity ?? undefined,
    });
  }, [rate, form]);

  async function change(values: ChangeFormValues) {
    if (!rate) return;
    const changed = await submit(
      () =>
        supersedeAccessorialRate(rate.id, {
          amount: Money.parse(String(values.amount), values.currency).toJSON(),
          freeQuantity: values.freeQuantity ?? null,
          effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
        }).then(() => true),
      (err) => apiErrorMessage(err) ?? 'Could not change the rate — check the fields and try again.',
    );
    if (!changed) return;
    toast.success('Accessorial rate changed');
    form.resetFields();
    onChanged();
    onClose();
  }

  return (
    <Drawer
      open={rate !== null}
      onClose={onClose}
      afterOpenChange={clearError}
      title={`Change ${type?.name ?? 'accessorial'} rate`}
      placement="right"
      size={440}
      destroyOnClose
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ChangeFormValues> form={form} layout="vertical" requiredMark={false} onFinish={change} disabled={submitting}>
        <AmountFields label="New price" isCurrencyLocked />
        <FreeQuantityField type={rate?.freeQuantity === null ? undefined : type} />
        <Form.Item name="effectiveDate" label="Takes over on" rules={[{ required: true, message: 'Choose a date' }]}>
          <DatePicker className="w-full" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Change rate
        </Button>
      </Form>
    </Drawer>
  );
}
